import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, testimonials, faqData } from '@/mocks/comics';

const aboutHeroBg = 'https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20corner%20with%20manga%20books%20scattered%20on%20a%20wooden%20table%2C%20soft%20sunlight%20through%20window%2C%20Japanese%20aesthetic%20interior%2C%20comfortable%20armchair%2C%20potted%20plant%2C%20peaceful%20atmosphere%2C%20cream%20and%20beige%20tones%2C%20no%20people%2C%20high%20quality%20photography&width=1920&height=500&seq=about-hero-manga&orientation=landscape';

const teamImg1 = 'https://readdy.ai/api/search-image?query=Friendly%20anime%20style%20manga%20editor%20character%20illustration%2C%20warm%20smile%2C%20holding%20a%20tablet%2C%20casual%20office%20outfit%2C%20clean%20white%20background%2C%20soft%20lighting%2C%20professional%20yet%20cute%20style&width=400&height=400&seq=team-illust-01&orientation=squarish';

const teamImg2 = 'https://readdy.ai/api/search-image?query=Anime%20style%20manga%20translator%20character%2C%20concentrated%20expression%2C%20working%20at%20desk%20with%20laptop%2C%20headphones%20around%20neck%2C%20clean%20white%20background%2C%20warm%20lighting%2C%20professional%20illustration&width=400&height=400&seq=team-illust-02&orientation=squarish';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: '关于我们', href: '#' },
  ];

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="px-4 md:px-6 py-3 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              {breadcrumbs.map((crumb, idx) => (
                <li key={crumb.label} className="flex items-center gap-2">
                  {idx > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
                  {crumb.href !== '#' ? (
                    <Link to={crumb.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground-800 font-medium">{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={aboutHeroBg}
            alt="关于古古漫画网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-2xl md:text-4xl text-background-50 mb-3">关于古古漫画</h1>
            <p className="text-sm md:text-base text-background-200 max-w-xl">
              致力于为漫画爱好者提供最优质的免费漫画阅读体验
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    古古漫画网成立于2020年，由一群热爱漫画的年轻人共同创建。我们深知漫画爱好者的需求——<strong>资源丰富、更新及时、阅读体验流畅</strong>。
                  </p>
                  <p>
                    经过数年的发展，古古漫画网已成为国内领先的漫画阅读平台之一，汇聚了<strong>超过50,000部</strong>国漫、日漫、韩漫资源，涵盖玄幻、都市、恋爱、科幻、少年、少女等各类题材。
                  </p>
                  <p>
                    我们坚持<strong>免费全集下拉式阅读</strong>的理念，让每一位漫画爱好者都能轻松享受阅读的乐趣。同时，我们与众多漫画作者和平台保持良好合作，确保每日更新最新连载。
                  </p>
                  <p>
                    古古漫画网的使命是——<strong>让好漫画触手可及</strong>。无论你身在何处，只需打开古古漫画网，就能进入精彩的漫画世界。
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-1 space-y-4">
                  <div className="rounded-lg overflow-hidden">
                    <img src={teamImg1} alt="古古漫画编辑团队" className="w-full h-auto object-cover" />
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img src={teamImg2} alt="古古漫画翻译团队" className="w-full h-auto object-cover" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Anime%20style%20manga%20reader%20enjoying%20comic%20book%2C%20relaxed%20happy%20expression%2C%20cozy%20atmosphere%2C%20warm%20lighting%2C%20cream%20background%2C%20soft%20colors%2C%20professional%20illustration%2C%20detailed%20style&width=400&height=600&seq=team-illust-03&orientation=portrait"
                      alt="古古漫画用户"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: '2020年', label: '创立年份' },
                { value: '50,000+', label: '漫画资源' },
                { value: '1,200万+', label: '月活用户' },
                { value: '每日更新', label: '连载速度' },
              ].map((item) => (
                <div key={item.label} className="text-center p-5 bg-background-50 rounded-lg border border-background-200">
                  <p className="font-heading text-2xl md:text-3xl text-primary-600">{item.value}</p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">我们的价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  icon: 'ri-heart-line',
                  title: '热爱漫画',
                  desc: '团队的每一位成员都是资深漫画迷，我们用心打造每一个细节，只为提供最好的阅读体验。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '品质保证',
                  desc: '严格筛选漫画资源，确保高清画质和准确翻译，让每一页漫画都值得细细品读。',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '极速更新',
                  desc: '与多家版权方和翻译组保持紧密合作，确保热门漫画章节在第一时间同步上线。',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '用户至上',
                  desc: '持续优化网站功能和界面设计，倾听用户反馈，不断提升产品体验。',
                },
                {
                  icon: 'ri-global-line',
                  title: '开放包容',
                  desc: '汇聚国漫、日漫、韩漫等全球优质漫画，满足不同读者的多样化需求。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '安全可靠',
                  desc: '严格遵守法律法规，保护用户隐私，打造安全放心的漫画阅读环境。',
                },
              ].map((item) => (
                <div key={item.title} className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-lg md:text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover" />
                    <div>
                      <p className="font-medium text-sm text-foreground-900">{t.name}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-500">support@gugumanhua.com</p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-chat-smile-2-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-2">意见反馈</h3>
                <p className="text-sm text-foreground-500">通过网站反馈表单提交您宝贵的意见</p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200 rounded-lg text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-group-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-2">商务合作</h3>
                <p className="text-sm text-foreground-500">business@gugumanhua.com</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">常见问题</h2>
            <div className="space-y-3">
              {faqData.map((faq, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-background-50/80 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <h4 className="font-heading text-sm md:text-base text-foreground-900 pr-4">
                      {faq.question}
                    </h4>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform flex-shrink-0 ${
                        openFaq === idx ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === idx && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}